/**
 * FERP Portal - Data-Driven Form Hook
 *
 * Combines useTenantSubgraphConfig with form schema loading to provide
 * truly data-driven form generation.
 *
 * FERP Principles:
 * - Data-driven: Entity types and form schemas from backend
 * - NO hardcoded form configuration
 * - Life-critical: Safety levels from SubgraphMetadata
 *
 * Usage:
 * ```tsx
 * const { entityTypes, getFormForEntity, loading } = useDataDrivenForm(tenantId);
 *
 * // Get form config for Patient entity
 * const patientForm = getFormForEntity('Patient');
 * // Returns: { subgraph: 'fhir', entityType: 'Patient', formSchemaId: '...' }
 * ```
 */

import { useMemo, useCallback } from 'react';
import {
  useTenantSubgraphConfig,
  type SubgraphEntityType,
  type TenantSubgraphConfig,
} from './useTenantSubgraphConfig';

// ============================================================================
// Types
// ============================================================================

export interface DataDrivenFormConfig {
  /** Subgraph this entity belongs to */
  subgraph: string;
  /** Entity type name */
  entityType: string;
  /** GraphQL type name */
  graphqlType: string;
  /** Form schema ID for loading form definition */
  formSchemaId: string | null;
  /** List schema ID for loading list definition */
  listSchemaId: string | null;
  /** Whether this entity is life-critical */
  isLifeCritical: boolean;
  /** Compliance frameworks */
  complianceFrameworks: string[];
  /** CRUD permission scopes */
  permissions: SubgraphEntityType['permissions'];
  /** Display field for lists/dropdowns */
  displayField: string;
  /** Plural name for UI */
  pluralName: string;
  /** Description */
  description: string;
}

export interface UseDataDrivenFormResult {
  /** All entity types available for this tenant */
  entityTypes: SubgraphEntityType[];

  /** Get entity types for a specific subgraph */
  getEntityTypesForSubgraph: (subgraphId: string) => SubgraphEntityType[];

  /** Get form config for an entity type by name */
  getFormForEntity: (entityName: string) => DataDrivenFormConfig | null;

  /** Get form config for an entity type by GraphQL type name */
  getFormByGraphQLType: (graphqlType: string) => DataDrivenFormConfig | null;

  /** Check if tenant can create this entity type */
  canCreate: (entityName: string) => boolean;

  /** Check if tenant can read this entity type */
  canRead: (entityName: string) => boolean;

  /** Check if tenant can update this entity type */
  canUpdate: (entityName: string) => boolean;

  /** Check if tenant can delete (archive) this entity type */
  canDelete: (entityName: string) => boolean;

  /** Full tenant config */
  config: TenantSubgraphConfig | null;

  /** Loading state */
  loading: boolean;

  /** Error state */
  error: Error | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for data-driven form generation.
 *
 * Provides access to entity types and form configurations based on
 * the tenant's enabled subgraphs.
 */
export function useDataDrivenForm(tenantId: string | null): UseDataDrivenFormResult {
  const {
    config,
    loading,
    error,
    hasPermission,
    getEntityType,
  } = useTenantSubgraphConfig(tenantId);

  // Get all entity types
  const entityTypes = useMemo(() => {
    return config?.allEntityTypes ?? [];
  }, [config]);

  // Create a map from entity name to subgraph
  const entityToSubgraphMap = useMemo(() => {
    if (!config) return new Map<string, string>();

    const map = new Map<string, string>();
    for (const subgraph of config.subgraphs) {
      // Find entity types that belong to this subgraph based on permissions prefix
      for (const entity of config.allEntityTypes) {
        // Check if entity permissions start with subgraph prefix
        const prefix = entity.permissions.read.split(':')[0];
        if (prefix === subgraph.subgraphId) {
          map.set(entity.name, subgraph.subgraphId);
        }
      }
    }
    return map;
  }, [config]);

  // Get entity types for a specific subgraph
  const getEntityTypesForSubgraph = useCallback(
    (subgraphId: string): SubgraphEntityType[] => {
      if (!config) return [];
      return config.allEntityTypes.filter((entity) => {
        const prefix = entity.permissions.read.split(':')[0];
        return prefix === subgraphId;
      });
    },
    [config]
  );

  // Get form config for an entity by name
  const getFormForEntity = useCallback(
    (entityName: string): DataDrivenFormConfig | null => {
      const entity = getEntityType(entityName);
      if (!entity) return null;

      const subgraph = entityToSubgraphMap.get(entityName) ?? 'unknown';

      return {
        subgraph,
        entityType: entity.name,
        graphqlType: entity.graphqlType,
        formSchemaId: entity.formSchemaId,
        listSchemaId: entity.listSchemaId,
        isLifeCritical: entity.isLifeCritical,
        complianceFrameworks: entity.complianceFrameworks,
        permissions: entity.permissions,
        displayField: entity.displayField,
        pluralName: entity.pluralName,
        description: entity.description,
      };
    },
    [getEntityType, entityToSubgraphMap]
  );

  // Get form config by GraphQL type name
  const getFormByGraphQLType = useCallback(
    (graphqlType: string): DataDrivenFormConfig | null => {
      if (!config) return null;

      const entity = config.allEntityTypes.find((e) => e.graphqlType === graphqlType);
      if (!entity) return null;

      return getFormForEntity(entity.name);
    },
    [config, getFormForEntity]
  );

  // Permission check helpers
  const canCreate = useCallback(
    (entityName: string): boolean => {
      const entity = getEntityType(entityName);
      if (!entity) return false;
      return hasPermission(entity.permissions.create);
    },
    [getEntityType, hasPermission]
  );

  const canRead = useCallback(
    (entityName: string): boolean => {
      const entity = getEntityType(entityName);
      if (!entity) return false;
      return hasPermission(entity.permissions.read);
    },
    [getEntityType, hasPermission]
  );

  const canUpdate = useCallback(
    (entityName: string): boolean => {
      const entity = getEntityType(entityName);
      if (!entity) return false;
      return hasPermission(entity.permissions.update);
    },
    [getEntityType, hasPermission]
  );

  const canDelete = useCallback(
    (entityName: string): boolean => {
      const entity = getEntityType(entityName);
      if (!entity) return false;
      return hasPermission(entity.permissions.delete);
    },
    [getEntityType, hasPermission]
  );

  return {
    entityTypes,
    getEntityTypesForSubgraph,
    getFormForEntity,
    getFormByGraphQLType,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    config,
    loading,
    error,
  };
}

export default useDataDrivenForm;
